import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { ArtistHubDirectorySection } from "@/features/artist-hub/artist-hub-directory-section";
import { getArtistHubQueryOptions } from "@/features/artist-hub/artist-hub-query";
import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

export function ArtistHubContent({ locale }: { locale: ViewerLocale }) {
  const t = useTranslations("artistHub");
  const { data } = useQuery(getArtistHubQueryOptions(locale));
  const directories = data?.directories ?? [];
  const genres = directories.filter((directory) => directory.family === "genre");
  const styles = directories.filter((directory) => directory.family === "style");

  return (
    <main className="relative isolate overflow-hidden px-4 pb-20 pt-12 sm:px-6 sm:pb-24 sm:pt-16 lg:px-8 lg:pb-28 lg:pt-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[38rem] bg-[radial-gradient(ellipse_at_12%_0%,hsl(var(--primary)/0.2),transparent_52%)]" />

      <div className="mx-auto max-w-[1440px]">
        <header className="max-w-5xl border-b border-border pb-10 sm:pb-12 lg:pb-16">
          <h1 className="text-balance text-5xl font-black tracking-[-0.04em] sm:text-6xl lg:text-7xl lg:leading-[0.96]">
            {t("title")}
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-xl">
            {t("description")}
          </p>
        </header>

        {directories.length ? (
          <div className="mt-10 space-y-14 sm:mt-12 sm:space-y-16 lg:mt-16">
            {genres.length ? (
              <ArtistHubDirectorySection
                title={t("genresTitle")}
                items={genres}
                directoryLabel={(label) => t("directoryLabel", { label })}
              />
            ) : null}
            {styles.length ? (
              <ArtistHubDirectorySection
                title={t("stylesTitle")}
                items={styles}
                directoryLabel={(label) => t("directoryLabel", { label })}
              />
            ) : null}
          </div>
        ) : (
          <p className="mt-10 border-y border-dashed border-border px-6 py-16 text-center text-muted-foreground sm:mt-12">
            {t("empty")}
          </p>
        )}
      </div>
    </main>
  );
}
